import { useEffect, type JSX } from 'react';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import type { ChatActionsContent } from '../../chatbot.types';
import { useChatbotEventBridge } from '../../ChatbotProvider';
import { useChatActionsViewModel } from './chat-actions.viewmodel';

type Props = {
  content: ChatActionsContent;
};

/**
 * Renders action buttons inside a chat bubble.
 * On click, emits the action's `event` on the ChatbotEventBridge.
 */
export const ChatActions = ({ content }: Props): JSX.Element => {
  const bridge = useChatbotEventBridge() ?? null;
  const [state, viewModel] = useChatActionsViewModel(content, bridge);

  // Keep viewmodel in sync when content is mutated externally (e.g. disablePreviousActions)
  useEffect(() => {
    viewModel.setContent(content);
  }, [content, content.disabled, content.selectedActionId, viewModel]);

  return (
    <>
      {state.text && (
        <div className='chatbot-markdown'>
          <Markdown remarkPlugins={[remarkBreaks]}>{state.text}</Markdown>
        </div>
      )}

      {state.actions.length > 0 && (
        <div className='chatbot-actions'>
          {state.actions.map((action) => {
            const Icon = action.icon;

            return (
              <button
                key={action.id}
                className={viewModel.getButtonClassName(action)}
                disabled={viewModel.isDisabled(action)}
                onClick={() => viewModel.handleClick(action)}
              >
                {typeof Icon === 'function' && <Icon className='chatbot-action-icon' />}
                {viewModel.getLabel(action)}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};
